import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import type { TicketStatus } from '../types';

const STEPS: { key: TicketStatus; color: string; bgActive: string; bgDone: string }[] = [
  { key: 'open', color: 'text-surface-600', bgActive: 'bg-surface-200 ring-surface-400', bgDone: 'bg-surface-400' },
  { key: 'in_progress', color: 'text-info-600', bgActive: 'bg-info-100 ring-info-500', bgDone: 'bg-info-500' },
  { key: 'waiting', color: 'text-warning-600', bgActive: 'bg-warning-100 ring-warning-500', bgDone: 'bg-warning-500' },
  { key: 'closed', color: 'text-success-600', bgActive: 'bg-success-100 ring-success-500', bgDone: 'bg-success-500' },
];

interface StatusStepperProps {
  currentStatus: TicketStatus;
  onStatusChange: (status: TicketStatus) => void;
  disabled?: boolean;
}

export default function StatusStepper({ currentStatus, onStatusChange, disabled }: StatusStepperProps) {
  const { t } = useTranslation();
  const currentIndex = STEPS.findIndex((s) => s.key === currentStatus);

  return (
    <div className="flex items-center w-full" dir="rtl">
      {STEPS.map((step, i) => {
        const isDone = i < currentIndex;
        const isCurrent = i === currentIndex;
        const label = t(`statusStepper.${step.key}`);

        return (
          <div key={step.key} className="flex items-center flex-1 last:flex-initial">
            <button
              onClick={() => !disabled && onStatusChange(step.key)}
              disabled={disabled}
              className={`flex flex-col items-center gap-1 group cursor-pointer disabled:cursor-not-allowed transition-all`}
              title={label}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all text-sm font-bold
                  ${isDone ? `${step.bgDone} text-white` : ''}
                  ${isCurrent ? `${step.bgActive} ring-2 ${step.color}` : ''}
                  ${!isDone && !isCurrent ? 'bg-surface-100 text-surface-400 group-hover:bg-surface-200' : ''}
                `}
              >
                {isDone ? <Check size={16} /> : i + 1}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap ${isCurrent ? step.color : 'text-surface-400'}`}>
                {label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mt-[-12px] transition-colors ${i < currentIndex ? step.bgDone : 'bg-surface-200'}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}
